"use client";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Grid } from "@radix-ui/themes";
import { LotQuery, LotWithSortingData } from "./page";
import { AuctionSettings, AuctionState, BiddingSchedule, BiddingScheduleRange, Lot } from "@prisma/client";
import LotPreview from "./LotPreview";
import { Skeleton } from "@/app/components/ui/skeleton";
import { fetcher } from "@/lib/utils";
import useSWR from "swr";
import { LotLiveData } from "@/app/api/auctions/[auctionId]/gridData/route";
import { drop, take } from "lodash";
import {
  AuctionFilterContext,
  AuctionFilterContextProps,
  Filter,
  FilterType,
} from "@/lib/context/AuctionFilterContext";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { WinningStatus } from "@/lib/enum/winStatus";
import { HttpStatusCode } from "axios";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import _ from "lodash";
import { debounce } from "lodash";
import { AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE } from "@/lib/constants";
import { SoftCloseContext } from "@/lib/context/SoftCloseContext";
import { getNthNextBid } from "@/lib/bidding/util";
interface Props {
  allLots: LotWithSortingData[];
  auctionStart: Date;
  auctionState: AuctionState;
  auctionSettings: AuctionSettings;
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
}

const DEBOUNCE_INTERVAL = 150; // ms
// Catalogue data can never fall more than 5 seconds out of sync.
const REFRESH_INTERVAL = 5000; // ms

// TODO: Paginate data-loading based off searchParams
const AuctionCatalogue = ({
  allLots,
  auctionStart,
  auctionState,
  auctionSettings,
  biddingSchedule,
}: Props) => {
  const searchParams = useSearchParams();
  const { auctionId } = useParams();

  const page = parseInt(searchParams.get("page") || "1");
  const pageSize = parseInt(
    searchParams.get("pageSize") ||
    AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE.toString()
  );

  const [lots, setLots] = useState<LotWithSortingData[]>(allLots);
  const auctionFilterContext = useContext(AuctionFilterContext);
  const [forceRender, setForceRender] = useState(0);
  const softCloseContext = useContext(SoftCloseContext);
  const router = useRouter();
  const [isLoadingLots, setIsLoadingLots] = useState(true);

  const {
    data: liveAuctionState,
    mutate: mutateAuctionState,
  } = useSWR<AuctionState>(
    `/api/auctions/${auctionId}/auctionState`,
    fetcher,
    {
      fallbackData: auctionState,
      revalidateOnMount: true,
    }
  );

  // TODO: Don't display grid times until this isValidating is false.
  const {
    data: lotGridData,
    isLoading,
    mutate: mutateLotGridData,
  } = useSWR<{ [key: number]: LotLiveData }>(
    `/api/auctions/${auctionId}/gridData`,
    fetcher,
    {
      refreshInterval: REFRESH_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  );

  const { data: winningStatus, mutate: mutateWinningStatus } = useSWR<{
    [key: number]: WinningStatus;
  }>(
    `/api/auctions/${auctionId}/gridData/winning-status`,
    async (url) => {
      const res = await fetch(url);
      if (res.status === HttpStatusCode.NoContent) {
        // Return null if 204 status code is received
        return null;
      }
      return res.json();
    },
    {
      refreshInterval: REFRESH_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const onAuctionStateChange = useCallback((payload: any) => {
    if (payload?.auctionState) {
      mutateAuctionState(payload.auctionState);
    }
  }, [mutateAuctionState]);

  const updateLotGridData = useCallback(
    (
      payload: any,
      lotId: number,
      updateFunction: () => Partial<LotLiveData>
    ) => {
      mutateLotGridData(
        (currentData: { [key: number]: LotLiveData } | undefined) => {
          if (!currentData) return currentData;
          if (!currentData[lotId]) return currentData;
          return {
            ...currentData,
            [lotId]: {
              ...currentData[lotId],
              ...updateFunction(),
            },
          };
        },
        false
      );
      mutateWinningStatus();
    },
    [mutateLotGridData, mutateWinningStatus]
  );

  const onLotChange = useCallback(
    (payload: any) => {
      if (payload?.endDate && payload?.id) {
        updateLotGridData(payload, payload.id, () => ({
          endDate: payload.endDate,
        }));
      }
    },
    [updateLotGridData]
  );

  const onHighestBidChange = useCallback((payload: any) => {
    mutateWinningStatus();
    if (payload?.amount && payload?.lotId) {
      updateLotGridData(
        payload,
        payload.lotId,
        () => ({
          highestBid: payload.amount,
          nextBid: getNthNextBid(payload.amount, biddingSchedule, 1)
        })
      );
    }
  }, [updateLotGridData, mutateWinningStatus, biddingSchedule]);

  const auctionTopic = `postgres.${process.env
    .NEXT_PUBLIC_DB_SCHEMA!}.Auction${auctionId}`;
  const lotTopic = `postgres.${process.env
    .NEXT_PUBLIC_DB_SCHEMA!}.Auction${auctionId}.Lot`;
  const highestBidTopic = `postgres.${process.env
    .NEXT_PUBLIC_DB_SCHEMA!}.Auction.HighestBid`;

  useSharedSubscription({
    topic: auctionTopic,
    onMessage: onAuctionStateChange,
  });

  useSharedSubscription({
    topic: highestBidTopic,
    onMessage: onHighestBidChange,
  });

  useSharedSubscription({ topic: lotTopic, onMessage: onLotChange });

  // TOOD: What happens if this fails?
  const { data: imageKeys } = useSWR(
    `/api/image/auctions/${auctionId}?limit=1`,
    fetcher
  );

  useEffect(() => {
    setForceRender((prev) => prev + 1);
  }, [softCloseContext?.newCloseCount]);

  const filteredLots = useMemo(() => {
    const initialFilteredLots = filterLots(allLots, auctionFilterContext);
    return initialFilteredLots.filter((lot) => {
      if (auctionState === AuctionState.POST_AUCTION) {
        const hasHighestBid = lot?.HighestBid?.length > 0;
        return auctionSettings.resultsPublished && hasHighestBid;
      }
      const endDate = lotGridData?.[lot.id]?.endDate || lot.endDate;
      return endDate && new Date().getTime() < new Date(endDate).getTime();
    });
  }, [allLots, auctionFilterContext, forceRender, lotGridData, auctionState, auctionSettings]);

  const sortedLots = useMemo(() => {
    return sortLots(filteredLots, searchParams.get("orderBy"));
  }, [filteredLots, searchParams]);

  const debouncedFilter = useCallback(() => {
    const filterFn = () => {
      auctionFilterContext?.setItemCount(filteredLots.length);

      if ((page - 1) * pageSize >= sortedLots.length && sortedLots.length > 0) {
        const params = new URLSearchParams(searchParams);
        params.set("page", "1");
        router.replace("?" + params.toString());
        return;
      }

      const paginatedLots = take(
        drop(sortedLots, (page - 1) * pageSize),
        pageSize
      );

      setLots(paginatedLots);
    };

    return debounce(filterFn, DEBOUNCE_INTERVAL);
  }, [
    auctionFilterContext,
    page,
    pageSize,
    router,
    searchParams,
    sortedLots,
    filteredLots,
    forceRender,
  ])();

  useEffect(() => {
    debouncedFilter();
  }, [debouncedFilter]);

  if (!liveAuctionState) {
    return null;
  }

  if (
    auctionState === AuctionState.POST_AUCTION &&
    !auctionSettings.resultsPublished
  ) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        Auction results have not yet been published.
      </div>
    );
  }

  if (lots.length === 0) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        No lots matching the filtering criteria were found.
      </div>
    );
  }

  return imageKeys ? (
    <Grid
      columns={{ initial: "2", sm: "3", md: "4" }}
      gapX={{ initial: "1", sm: "7" }}
      gapY={{ initial: "9", sm: "8" }}
      className="w-full"
    >
      {lots.map((lot, index) => {
        if (!lotGridData?.[lot.id])
          return (
            <Skeleton key={index} className="w-full h-[350px] rounded-none" />
          );

        return (
          <LotPreview
            key={lot.id}
            auctionId={auctionId as string}
            lot={lot}
            imageKey={imageKeys?.[lot.id]}
            polledData={lotGridData[lot.id]}
            followers={lot.Subscription.length ?? 0}
            winningStatus={winningStatus?.[lot.id]}
            auctionStart={auctionStart}
            auctionState={liveAuctionState}
            loading={isLoading}
          />
        );
      })}
    </Grid>
  ) : (
    <Grid
      columns={{ xs: "2", sm: "3", md: "4" }}
      gapX="7"
      gapY="8"
      width="auto"
    >
      {lots.map((_, index) => (
        <Skeleton key={index} className="w-full h-[350px] rounded-none" />
      ))}
    </Grid>
  );
};

function filterLots(
  lots: LotWithSortingData[],
  auctionFilterContext: AuctionFilterContextProps | null
) {
  let ret = filterByCategory(
    lots,
    auctionFilterContext?.getFiltersOfType(FilterType.CATEGORY)
  );
  ret = filterByBudget(
    ret,
    auctionFilterContext?.getFiltersOfType(FilterType.BUDGET)
  );
  return filterBySearch(ret, auctionFilterContext?.search);
}

const filterByCategory = (
  lots: LotWithSortingData[],
  categoryFilters: Filter[] | undefined
) => {
  if (!categoryFilters || categoryFilters.length === 0) return lots;
  return lots.filter((lot) =>
    categoryFilters.some((filter) => filter.label === lot.category)
  );
};

const filterByBudget = (
  lots: LotWithSortingData[],
  budgetFilters: Filter[] | undefined
) => {
  if (!budgetFilters || budgetFilters.length === 0) return lots;

  return lots.filter((lot) => {
    const currentBid = lot?.HighestBid?.[0]?.amount ?? lot.startBid;

    // Huge code smell here.
    return budgetFilters.every((filter) => {
      const label = filter.label.toLowerCase();
      if (label.startsWith("budget: above $")) {
        const threshold = parseInt(label.replace("budget: above $", ""));
        return currentBid > threshold;
      } else if (label.startsWith("budget: below $")) {
        const threshold = parseInt(label.replace("budget: below $", ""));
        return currentBid && currentBid < threshold;
      } else if (label.startsWith("budget: $")) {
        const [min, max] = label
          .replace("budget: $", "")
          .split(" to $")
          .map(Number);
        return currentBid >= min && currentBid <= max;
      }
      return true;
    });
  });
};

const filterBySearch = (
  lots: LotWithSortingData[],
  searchTerm: string | undefined
) => {
  if (!searchTerm) return lots;

  const numberResults = searchByNumber(searchTerm, lots);
  if (numberResults) return numberResults;

  const searchableFields = ["title", "description", "category"];
  return lots.filter((lot) =>
    searchableFields.some((field) =>
      matchesSearchTerm(lot, field as keyof Lot, searchTerm)
    )
  );
};

const matchesSearchTerm = (
  lot: LotWithSortingData,
  field: keyof Lot,
  searchTerm: string
) => {
  const value = lot[field];
  return (
    typeof value === "string" &&
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

// Check if searchTerm is numeric and within valid catalog number range,
// If so, return the lot with the matching catalog number
const searchByNumber = (searchTerm: string, lots: LotWithSortingData[]) => {
  const searchNumber = Number(searchTerm);
  if (!isNaN(searchNumber)) {
    const catalogNumbers = lots.map((lot) => lot.catalogNumber ?? 0);
    const minCatalog = Math.min(...catalogNumbers);
    const maxCatalog = Math.max(...catalogNumbers);

    if (searchNumber >= minCatalog && searchNumber <= maxCatalog) {
      return lots.filter((lot) => lot.catalogNumber === searchNumber);
    }
  }
  return null;
};

function sortLots(lots: LotWithSortingData[], sortParam: string | null) {
  const [field, order] = sortParam ? sortParam.split(",") : ["", ""];

  if (order !== "asc" && order !== "desc")
    return _.orderBy(lots, [(lot) => lot.catalogNumber], ["asc"]);

  switch (field) {
    case "lot":
      return _.orderBy(lots, [(lot) => lot.catalogNumber], [order]);
    case "activeBid":
      return _.orderBy(lots,
        [
          // First sort by whether there is a highest bid (creates two groups)
          (lot) => lot.HighestBid.length === 0,
          // Within groups, sort by highest bid amount or start bid
          (lot) => lot.HighestBid.length > 0 ? lot.HighestBid[0].amount : lot.startBid,
          // Maintain consistent ordering within equal values
          (lot) => lot.catalogNumber
        ],
        [
          'asc', // Keep bids with HighestBid first
          order,
          'asc'
        ]);
    case "bid":
      return _.orderBy(
        lots,
        [
          (lot) => {
            if (lot.HighestBid.length > 0) {
              return lot.HighestBid[0].amount;
            }
            if (lot.endDate && (new Date()).getTime() > (new Date(lot.endDate)).getTime()) {
              return -Infinity;
            }
            return lot.startBid;
          },
          (lot) => lot.catalogNumber,
        ],
        [order, "asc"]
      );
    case "bidCount":
      return _.orderBy(
        lots,
        [(lot) => lot.Bid.length, (lot) => lot.catalogNumber],
        [order, "asc"]
      );
    case "watchCount":
      return _.orderBy(
        lots,
        [(lot) => lot.Subscription.length, (lot) => lot.catalogNumber],
        [order, "asc"]
      );
    default:
      return lots;
  }
}

export default AuctionCatalogue;
